import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  MessageCircle, X, Send, Loader2, ThumbsUp, ThumbsDown, 
  AlertTriangle, HelpCircle, ChevronRight, LifeBuoy, Bot
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupportBot = () => {
  const { token } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [troubleshootingScenarios, setTroubleshootingScenarios] = useState([]);
  const messagesEndRef = useRef(null);

  // Hide SupportBot on mobile PWA routes to avoid overlap with bottom navigation
  const hiddenRoutes = ['/trust-app', '/exec-app'];
  const shouldHide = hiddenRoutes.some(route => location.pathname.startsWith(route));

  useEffect(() => {
    if (isOpen && messages.length === 0 && token) {
      // Initial greeting
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: "Hi! I'm your Simplif-i assistant. I can help you with:\n\n• **How to use features** - navigation, guides, tips\n• **Troubleshooting** - fix common issues\n• **Compliance guidance** - ISO, GDPR, SOC 2 best practices\n\nWhat can I help you with today?",
        type: 'greeting'
      }]);
      loadTroubleshootingScenarios();
    }
  }, [isOpen, token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadTroubleshootingScenarios = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/support-bot/troubleshoot`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTroubleshootingScenarios(res.data.scenarios || []);
    } catch {}
  };

  // Don't render if not authenticated or on mobile PWA routes
  if (!token || shouldHide) {
    return null;
  }

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: input
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const endpoint = conversationId 
        ? `${API}/support-bot/chat/${conversationId}/continue`
        : `${API}/support-bot/chat`;

      const res = await axios.post(endpoint, {
        message: input,
        context: {
          current_page: window.location.pathname
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!conversationId) {
        setConversationId(res.data.conversation_id);
      }

      const botMessage = {
        id: res.data.message_id,
        role: 'assistant',
        content: res.data.response,
        type: res.data.type,
        category: res.data.category,
        suggestedActions: res.data.suggested_actions,
        canEscalate: res.data.can_escalate
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: "I'm having trouble connecting. Please try again or use the escalation option.",
        type: 'error'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const escalate = async () => {
    if (!conversationId) {
      toast.error('Start a conversation first');
      return;
    }

    try {
      await axios.post(`${API}/support-bot/escalate`, {
        conversation_id: conversationId,
        reason: 'User requested escalation'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages(prev => [...prev, {
        id: `escalate_${Date.now()}`,
        role: 'assistant',
        content: "I've escalated your issue to our support team. They'll respond within 24 hours. You'll receive an email confirmation shortly.",
        type: 'escalation'
      }]);

      toast.success('Escalated to support team');
    } catch (error) {
      toast.error('Failed to escalate');
    }
  };

  const submitFeedback = async (messageId, helpful) => {
    if (!conversationId) return;

    try {
      await axios.post(`${API}/support-bot/feedback`, {
        conversation_id: conversationId,
        message_id: messageId,
        helpful
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, feedbackGiven: true } : m
      ));
    } catch {}
  };

  const startTroubleshooting = (scenario) => {
    setShowTroubleshooting(false);
    setInput(`I need help with: ${scenario.name}`);
    sendMessage();
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>');
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-50"
        data-testid="support-bot-trigger"
      >
        <MessageCircle className="h-6 w-6" />
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-6 right-6 w-96 h-[500px] flex flex-col shadow-2xl z-50 overflow-hidden" data-testid="support-bot-panel">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-primary text-primary-foreground">
        <div className="flex items-center space-x-2">
          <Bot className="h-5 w-5" />
          <span className="font-semibold">Support Assistant</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="h-8 w-8 p-0 hover:bg-white/20">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Quick Actions */}
      <div className="flex items-center space-x-2 p-2 border-b overflow-x-auto">
        <Button 
          variant="outline" 
          size="sm" 
          className="shrink-0 text-xs"
          onClick={() => setShowTroubleshooting(!showTroubleshooting)}
        >
          <HelpCircle className="h-3 w-3 mr-1" />
          Troubleshoot
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          className="shrink-0 text-xs"
          onClick={escalate}
        >
          <LifeBuoy className="h-3 w-3 mr-1" />
          Escalate
        </Button>
      </div>

      {/* Troubleshooting Dropdown */}
      {showTroubleshooting && (
        <div className="p-2 border-b bg-muted/50 max-h-32 overflow-y-auto">
          <p className="text-xs text-muted-foreground mb-2">Common issues:</p>
          <div className="space-y-1">
            {troubleshootingScenarios.map((scenario) => (
              <button
                key={scenario.id}
                onClick={() => startTroubleshooting(scenario)}
                className="w-full text-left text-sm p-2 rounded hover:bg-muted flex items-center justify-between"
              >
                {scenario.name}
                <ChevronRight className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-1' : 'order-2'}`}>
              <div
                className={`rounded-lg p-3 ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : msg.type === 'error'
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    : msg.type === 'escalation'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : msg.type === 'cannot_develop'
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                    : 'bg-muted'
                }`}
              >
                <div 
                  className="text-sm"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatMessage(msg.content)) }}
                />
              </div>

              {/* Type badges */}
              {msg.type === 'cannot_develop' && (
                <Badge variant="outline" className="mt-1 text-xs">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Outside scope
                </Badge>
              )}

              {/* Suggested Actions */}
              {msg.suggestedActions?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {msg.suggestedActions.map((action, i) => (
                    <Button 
                      key={i} 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start text-xs"
                    >
                      {action.label}
                    </Button>
                  ))}
                </div>
              )}

              {/* Feedback */}
              {msg.role === 'assistant' && msg.type !== 'greeting' && msg.type !== 'escalation' && !msg.feedbackGiven && (
                <div className="flex items-center space-x-1 mt-2">
                  <span className="text-xs text-muted-foreground">Helpful?</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={() => submitFeedback(msg.id, true)}
                  >
                    <ThumbsUp className="h-3 w-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={() => submitFeedback(msg.id, false)}
                  >
                    <ThumbsDown className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg p-3">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t">
        <div className="flex space-x-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask a question..."
            disabled={loading}
            className="flex-1"
            data-testid="support-bot-input"
          />
          <Button 
            onClick={sendMessage} 
            disabled={loading || !input.trim()}
            size="icon"
            data-testid="support-bot-send"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          I can help with existing features. For new requests, please escalate.
        </p>
      </div>
    </Card>
  );
};

export default SupportBot;
